import type { OHLCVPoint } from '../../api/types';

// ─── What: candlestick geometry ───────────────────────────────────────────────

export interface CandleGeom {
    x: number;
    width: number;
    bodyY: number;
    bodyH: number;
    wickTop: number;
    wickBottom: number;
    up: boolean;
}

export const MIN_BODY_PX = 1;

// ─── How it works ─────────────────────────────────────────────────────────────

/** Compute SVG geometry for candlesticks from OHLCV data and chart scales. */
export function computeCandleGeometry(
    data: OHLCVPoint[],
    xScale: (t: Date) => number,
    yScale: (v: number) => number,
    resolutionMin: number
): CandleGeom[] {
    if (data.length === 0) return [];
    const intervalMs = resolutionMin * 60_000;
    return data.map(d => {
        const x = xScale(d.t);
        const x2 = xScale(new Date(d.t.getTime() + intervalMs));
        // Full interval width (no upper cap) so adjacent candles always touch,
        // even when zoomed in where the per-interval pixel span grows large.
        const fullW = Math.abs(x2 - x);
        const width = Math.max(MIN_BODY_PX, fullW);
        const yOpen = yScale(d.open);
        const yClose = yScale(d.close);
        return {
            x,
            width,
            bodyY: Math.min(yOpen, yClose),
            bodyH: Math.max(1, Math.abs(yClose - yOpen)),
            wickTop: yScale(d.high),
            wickBottom: yScale(d.low),
            up: d.close >= d.open,
        };
    });
}

/** Candlesticks are only meaningful for a single market at a time. */
export function canShowCandles(selectedConditionIds: string[]): boolean {
    return selectedConditionIds.length === 1;
}

// ─── Which markets are on screen ──────────────────────────────────────────────
//
// The chart and EventView both need this answer — the chart to gate
// candlesticks, EventView to decide whether a NO-side fetch is worth making.
// One implementation so the two can never drift apart.

/** Condition ids actually drawn right now: has data, and passes any highlight. */
export function displayedConditionIds(
    series: { conditionId: string; data: unknown[] }[],
    highlightedMarkets?: Set<string> | null
): string[] {
    return series
        .filter(
            s =>
                s.data.length > 0 &&
                (!highlightedMarkets || highlightedMarkets.has(s.conditionId))
        )
        .map(s => s.conditionId);
}

/** The one displayed market, or null when zero or many are drawn. */
export function singleDisplayedMarket(
    series: { conditionId: string; data: unknown[] }[],
    highlightedMarkets?: Set<string> | null
): string | null {
    const ids = displayedConditionIds(series, highlightedMarkets);
    return ids.length === 1 ? ids[0] : null;
}

/**
 * The complementary outcome's series: price mirrors about 0.5, and the extremes
 * swap — the mirror of a bar's high is its low. Used for the NO mirror line, and
 * as the fallback when a real NO-side fetch comes back empty.
 */
export function flipOutcomeSeries(points: OHLCVPoint[]): OHLCVPoint[] {
    return points.map(d => ({
        ...d,
        open: 1 - d.open,
        close: 1 - d.close,
        high: 1 - d.low,
        low: 1 - d.high,
        // Price mirrors; volume does not. The complement's volume is a separate
        // measurement that only a real fetch can supply, and carrying the YES
        // figure across would label one side's volume as the other's in the
        // candle tooltip.
        vol: 0,
    }));
}
